import React, { forwardRef } from 'react';
import { IconButton as MuiIconButton } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useDsTokens } from '../theme/useDsTokens';
import { DsResolvedTokens } from '../theme/tokens';
import { cn } from '../utils/cn';
import { DsPrimitiveProps, IconButtonSize, IconButtonVariant } from './types';

export interface IconButtonProps extends DsPrimitiveProps {
  variant?: IconButtonVariant;
  size?: IconButtonSize;
  /** Required for accessibility — icon-only actions */
  'aria-label': string;
  type?: 'button' | 'submit' | 'reset';
  children: React.ReactElement;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  onFocus?: React.FocusEventHandler<HTMLButtonElement>;
  onBlur?: React.FocusEventHandler<HTMLButtonElement>;
}

interface RootStyleProps {
  tokens: DsResolvedTokens;
  iconColor: string;
  hitSize: number;
  disabled: boolean;
}

function resolveIconColor(variant: IconButtonVariant, tokens: DsResolvedTokens): string {
  switch (variant) {
    case 'primary':
      return tokens.brandPrimary;
    case 'danger':
      return tokens.statusError;
    case 'default':
      return tokens.textSecondary;
    case 'ghost':
    default:
      return tokens.textPrimary;
  }
}

const useStyles = makeStyles({
  root: (props: RootStyleProps) => ({
    padding: 0,
    width: props.hitSize,
    height: props.hitSize,
    minWidth: props.hitSize,
    minHeight: props.hitSize,
    borderRadius: props.tokens.radiusMd,
    color: props.iconColor,
    opacity: props.disabled ? props.tokens.opacityDisabled : 1,
    transition: `background-color ${props.tokens.motionDurationMicro}ms ${props.tokens.motionEasingStandard}`,
    '&:hover': {
      backgroundColor: props.tokens.interactionHover,
    },
    '&:active': {
      backgroundColor: props.tokens.interactionActive,
    },
    '&.Mui-focusVisible': {
      outline: `${props.tokens.borderWidthStrong}px solid ${props.tokens.borderFocus}`,
      outlineOffset: 2,
    },
    '&:focus': {
      outline: 'none',
    },
  }),
  iconBox: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
});

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  {
    id,
    className,
    disabled = false,
    variant = 'default',
    size = 'default',
    type = 'button',
    children,
    onClick,
    onFocus,
    onBlur,
    'aria-label': ariaLabel,
    'data-testid': testId,
  },
  ref
) {
  const tokens = useDsTokens();
  const hitSize = tokens.touchTargetMin;
  const visualSize = size === 'dense' ? tokens.space4 : tokens.space3;
  const iconPx = size === 'dense' ? tokens.space2 : tokens.space3;

  const classes = useStyles({
    tokens,
    iconColor: resolveIconColor(variant, tokens),
    hitSize,
    disabled,
  });

  return (
    <MuiIconButton
      ref={ref}
      id={id}
      type={type}
      disabled={disabled}
      aria-label={ariaLabel}
      className={cn(classes.root, className)}
      data-testid={testId}
      onClick={onClick}
      onFocus={onFocus}
      onBlur={onBlur}
    >
      <span
        className={classes.iconBox}
        style={{ width: visualSize, height: visualSize, fontSize: iconPx }}
      >
        {children}
      </span>
    </MuiIconButton>
  );
});

IconButton.displayName = 'IconButton';
